/**
 * Zone.js patch flags — applied *before* `zone.js` is imported in
 * {@link ../polyfills.ts}.
 *
 * These flags drop expensive patches we don't need so the storefront critical
 * chain doesn't pay 200-500 ms of zone.js startup on mobile (Lighthouse QA run
 * showed `polyfills*.js` at ~3.5 s CPU / ~2.4 s eval).
 *
 * Conservative scope: we only disable patches whose callers were audited and
 * found to NOT depend on auto change-detection. `MutationObserver` /
 * `FileReader` etc. stay patched because third-party libraries may use them.
 *
 * Reference: https://github.com/angular/angular/blob/main/packages/zone.js/MODULE.md
 */

declare global {
  interface Window {
    __Zone_disable_requestAnimationFrame?: boolean;
    __Zone_disable_canvas?: boolean;
    __Zone_disable_customElements?: boolean;
    __Zone_disable_IntersectionObserver?: boolean;
    __Zone_disable_ResizeObserver?: boolean;
    __Zone_disable_PerformanceObserver?: boolean;
    __zone_symbol__UNPATCHED_EVENTS?: string[];
  }
}

if (typeof window !== 'undefined') {
  /**
   * `requestAnimationFrame` doesn't need to re-enter NgZone — Angular's change
   * detection runs on macrotasks and microtasks; rAF callbacks should run
   * outside the zone for free 60fps work (image gallery scroll, animations).
   */
  window.__Zone_disable_requestAnimationFrame = true;

  /** No 2D/WebGL canvas in v3. */
  window.__Zone_disable_canvas = true;

  /** No `<x-foo>` custom elements / web components in v3. */
  window.__Zone_disable_customElements = true;

  /**
   * Audited usages:
   * - `utility.service.ts` `appearOnScroll` — only adds a CSS class + mounts Splide;
   *   no Angular bindings rely on CD firing here.
   * - `storefront-pdp-image-gallery.component.ts` `ResizeObserver` — only schedules
   *   a `requestAnimationFrame` measurement; no template state changes inside the
   *   callback.
   * - `PerformanceObserver` — only used in our own helpers (`after-lcp-idle`,
   *   `mixpanel.service`) which schedule work via `requestIdleCallback` /
   *   `setTimeout`, never directly mutate template-bound state.
   */
  window.__Zone_disable_IntersectionObserver = true;
  window.__Zone_disable_ResizeObserver = true;
  window.__Zone_disable_PerformanceObserver = true;

  /**
   * Bypass NgZone for high-frequency hover / pointer / touch movement events.
   * These fire 60+ times per second on a scrolling mobile page; without this,
   * every fired event triggers a full Angular change-detection cycle.
   *
   * Notably we do NOT unpatch:
   * - `scroll` — `design-groups.onWindowScroll`,
   *   `storefront-pdp-image-gallery.onMobGalleryScroll`, and the size-chart
   *   dialog scroll handlers all mutate template-bound state under `OnPush`
   *   and rely on the scroll macrotask driving CD.
   * - `touchstart` / `touchend` — `(touchstart)` / `(touchend)` template
   *   bindings on the storefront PDP image popup and the gallery's raw
   *   `el.addEventListener('touchend', ...)` swipe handler both call
   *   `cdr.markForCheck()` and rely on Angular running CD afterwards. Leaving
   *   these patched costs ~one extra CD per swipe, which is negligible.
   *
   * Components that need CD on touchmove / wheel can opt back in via
   * `ngZone.run(...)` (none on the storefront critical path today).
   */
  window.__zone_symbol__UNPATCHED_EVENTS = [
    'mousemove',
    'pointermove',
    'pointerover',
    'pointerout',
    'wheel',
    'touchmove',
  ];
}

export {};
